import { ENDPOINT } from '~/constants';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import {
  type GetReprocessingInvoicesCountsPayload,
  type ReprocessingInvoicesCountsResponse,
} from './types';
import { zodReprocessingInvoicesCountsPayloadSchema } from './validations';

/**
 * kyClient automatically converts keys to camelCase.
 * Unfortunately, this destroys the format of the timestamps which are used as keys in the response!
 * Instead of adding another costly check to the kyClient in general, we'll just fix those timestamps here.
 */
function fixTimestampKey(date: string) {
  const year = date.slice(0, 4);
  const month = date.slice(4, 6);
  const day = date.slice(6, 8);
  const time = date.slice(9);

  return `${year}-${month}-${day}T${time}`;
}

/**
 * Fetches aggregated invoice process counts from the API.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoice/operation/get_aggregated_invoice_processes_invoice_process_aggregated_get
 */
export async function fetchReprocessingInvoicesCounts(
  searchParams: GetReprocessingInvoicesCountsPayload,
): Promise<ReprocessingInvoicesCountsResponse> {
  try {
    const response = await vestigasApi
      .get(ENDPOINT.INVOICE_REPROCESSING.GET_COUNTS(), {
        searchParams,
        validate: { searchParams: zodReprocessingInvoicesCountsPayloadSchema },
      })
      .json<ReprocessingInvoicesCountsResponse>();

    // Fix timestamp keys
    const fixedResponse =
      response?.map((item) =>
        Object.fromEntries(
          Object.entries(item ?? {}).map(([date, value]) => [
            fixTimestampKey(date),
            value,
          ]),
        ),
      ) ?? [];

    return fixedResponse;
  } catch (error) {
    log.error({
      message: 'Error fetching reprocessing invoice counts',
      error,
    });

    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 422: {
          errorMessage = i18n.t(
            ($) =>
              $.invoiceReprocessingQueries.fetchReprocessingInvoicesCounts
                .error['422'],
            { ns: 'data' },
          );
          break;
        }

        default: {
          errorMessage = i18n.t(
            ($) =>
              $.invoiceReprocessingQueries.fetchReprocessingInvoicesCounts.error
                .generic,
            { ns: 'data' },
          );
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
}
